import { UseSetupCallbackCB, UserQuestionnaireType } from '@/components/decision-tree';
import { ROUTE_DASHBOARD } from '@/constants/routes';
import { TOAST_DEFAULT_OPTIONS } from '@/constants/toast';
import useAnalyticsCalls from '@/hooks/use-analytics';
import { useSetFooterIsVisible } from '@/queries/layout.queries';
import { useAnswerQuestionnaire, useGetQuestionnaire } from '@/queries/questionnaire.queries';
import { getRoute, getRouteLoadingScreen } from '@/utils';
import { useToast } from '@cmpsr/components';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Status } from './types';

export default function useQuestionnaireController() {
  const { data: userQuestionnaire } = useGetQuestionnaire();
  const [status, setStatus] = useState<Status | null>(
    _getInitialStatus(userQuestionnaire?.questionnaireStatistics?.answeredQuestionsCount),
  );
  const navigate = useNavigate();
  const analytics = useAnalyticsCalls();
  const mutateFooterIsVisible = useSetFooterIsVisible();

  const uploadAnswerMutation = useAnswerQuestionnaire();
  const toast = useToast();

  const startQuestionnaire = () => setStatus(Status.inProgress);

  function handleFinishIntroWizard() {
    startQuestionnaire();
  }

  // Update the status when the user data is fetched
  // Prevent the questionnaire from re-starting if the status is already in progress
  useEffect(() => {
    if (userQuestionnaire && status !== Status.inProgress) {
      setStatus(_getInitialStatus(userQuestionnaire?.questionnaireStatistics?.answeredQuestionsCount));
    }
  }, [userQuestionnaire]);

  useEffect(() => {
    if (status === Status.ready) analytics.callOnboardingStarted();
    if (status === Status.completed) {
      analytics.callOnboardingCompleted();
      navigate(getRouteLoadingScreen(getRoute(ROUTE_DASHBOARD), true));
    }
  }, [status]);

  const backOnFirstSurveyItem = () => {
    navigate(getRoute(ROUTE_DASHBOARD));
  };

  const uploadAnswers: UseSetupCallbackCB = async (surveyItemId, answer): Promise<UserQuestionnaireType | null> => {
    try {
      const resultQuestionnaire = await uploadAnswerMutation.mutateAsync({ surveyItemId, answer });

      if (resultQuestionnaire.nextSurveyItem.surveyItemId === null) {
        console.log('Questionnaire finished');
        setStatus(Status.completed);
        return null;
      }
      return resultQuestionnaire;
    } catch (error: any) {
      console.error(error);
      toast({
        ...TOAST_DEFAULT_OPTIONS,
        title: `Error ${error.response?.data?.statusCode ?? ''}`,
        description: error.response?.data?.message ?? error.message,
        status: 'error',
      });

      return userQuestionnaire ?? null;
    }
  };

  useEffect(() => {
    mutateFooterIsVisible.mutateAsync(false);
    return () => {
      mutateFooterIsVisible.mutateAsync(true);
    };
  }, []);

  return {
    backOnFirstSurveyItem,
    handleFinishIntroWizard,
    status,
    uploadAnswers,
    userQuestionnaire,
  };
}

function _getInitialStatus(answeredQuestionsCount?: number) {
  if (typeof answeredQuestionsCount === 'undefined') {
    return null;
  }
  return answeredQuestionsCount === 0 ? Status.ready : Status.inProgress;
}
